import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { apiFetchJson } from "../apiClient";
import { capabilityHint, kindHasCapability, summarizeHealth, summarizeRun } from "../utils/connectionForm";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import { HEALTH_BADGE, formatWhen } from "./CapabilityCards";
import ConnectionDetail from "./ConnectionDetail";
import NewConnectionForm from "./NewConnectionForm";
import RuntimeConnections from "./RuntimeConnections";
import "./AdminCollections.css";
import "./AdminConnectors.css";

const POLL_MS =
  Number((window._env_ && window._env_.REACT_APP_CONNECTOR_POLL_MS) ||
    process.env.REACT_APP_CONNECTOR_POLL_MS) || 5000;

const hasSource = (c) => kindHasCapability(c, "source");

const HTTP_NOT_FOUND = 404;

/** The state of one connection in the list: whether it is disabled, then the
 *  sync state of a source, else the result of its last health test. */
function ConnectionStatus({ connection: c }) {
  const { t } = useTranslation();
  const health = summarizeHealth(c.last_health, t);
  return (
    <div className="cx-status">
      {!c.enabled && <span className="ds-badge">{t("adminConnectors.disabledBadge")}</span>}
      {hasSource(c) ? (
        <span data-testid={`status-${c.id}`} className={statusBadgeClass(c.status)}>{statusLabel(c.status)}</span>
      ) : (
        <span className={HEALTH_BADGE[health.tone] || HEALTH_BADGE.neutral}>{health.label}</span>
      )}
      {hasSource(c) && c.last_error && <div className="ac-cell-error">{c.last_error}</div>}
    </div>
  );
}

/** When the connection last did something: its last sync for a source, its
 *  last health test otherwise. */
function LastActivity({ connection: c }) {
  const { t } = useTranslation();
  if (hasSource(c)) {
    return (
      <>
        {c.last_sync_at ? formatWhen(c.last_sync_at) : t("adminConnectors.never")}
        <div className="ds-meta ds-mono">{summarizeRun(c.last_summary)}</div>
      </>
    );
  }
  return c.last_health_at ? formatWhen(c.last_health_at) : "—";
}

/** Admin "Connectors" page (Norma feature 13): configure external systems,
 *  trigger syncs and manage who may use each connection. Credentials are
 *  write-only — the API never returns them. The list is cursor-paginated
 *  ("load more") and polls while any connector is syncing. "New connection"
 *  opens the create form (it is open by itself while there is nothing to
 *  list). "Manage" opens the detail panel next to the list. The panel shows
 *  the connection from the list while it is on a loaded page and its own copy
 *  (fetched by id) otherwise, so a reload of page 1 never loses a row that
 *  came in through "Load more"; it closes only when the server answers 404
 *  for that connection. The platform's own systems are listed read-only below. */
export default function AdminConnectors() {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openCopy, setOpenCopy] = useState(null);   // the open connection fetched by id
  const [showCreate, setShowCreate] = useState(false);
  const generation = useRef(0);
  const openGeneration = useRef(0);
  const openIdRef = useRef(null);
  openIdRef.current = openId;

  const openConnection = openId === null
    ? null
    : connectors.find((c) => c.id === openId) || (openCopy && openCopy.id === openId ? openCopy : null);
  const openKind = openConnection ? kinds.find((k) => k.kind === openConnection.kind) : undefined;
  const kindLabel = (c) => kinds.find((k) => k.kind === c.kind)?.label || c.kind;

  const isEmpty = !loading && connectors.length === 0;
  const createOpen = showCreate || isEmpty;

  const load = useCallback(async () => {
    const gen = ++generation.current;
    try {
      const data = await apiFetchJson("/api/v1/connections");
      if (gen !== generation.current) return;
      setConnectors(data?.items || []);
      setNextCursor(data?.next_cursor || null);
      setError("");
    } catch (e) {
      if (gen === generation.current) setError(e.message);   // keep the previous list
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    apiFetchJson("/api/v1/connection-kinds")
      .then((d) => {
        const payload = d || {};
        setKinds(payload.kinds || []);
        setRoles(payload.roles || []);
      })
      .catch((e) => setError(e.message));
  }, [load]);

  const openPanel = (connection) => {
    openGeneration.current += 1;             // drops the answers meant for the previous panel
    setOpenId(connection.id ?? null);
    setOpenCopy(connection);
  };

  const closePanel = useCallback(() => {
    openGeneration.current += 1;
    setOpenId(null);
    setOpenCopy(null);
  }, []);

  // Re-reads the open connection by id. Only a 404 (it was deleted) closes
  // the panel; any other failure keeps it and is shown in the page alert.
  const refreshOpen = useCallback(async () => {
    const id = openIdRef.current;
    if (id === null) return;
    const gen = ++openGeneration.current;
    try {
      const data = await apiFetchJson(`/api/v1/connections/${encodeURIComponent(id)}`);
      if (gen !== openGeneration.current) return;
      if (data && data.id === id) setOpenCopy(data);
    } catch (e) {
      if (gen !== openGeneration.current) return;
      if (e && e.status === HTTP_NOT_FOUND) closePanel();
      else setError(e.message);
    }
  }, [closePanel]);

  const refreshAll = useCallback(() => Promise.all([load(), refreshOpen()]), [load, refreshOpen]);

  const anyRunning = connectors.some((c) => hasSource(c) && c.status === "running");
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(() => {
      // the open panel is refreshed by id too: page 1 may not carry its row
      load();
      refreshOpen();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, load, refreshOpen]);

  const loadMore = async () => {
    try {
      const data = await apiFetchJson(`/api/v1/connections?cursor=${encodeURIComponent(nextCursor)}`);
      setConnectors((prev) => [...prev, ...(data?.items || [])]);
      setNextCursor(data?.next_cursor || null);
    } catch (e) {
      setError(e.message);
    }
  };

  const syncNow = async (id) => {
    setBusyId(id);
    try {
      await apiFetchJson(`/api/v1/connections/${id}/sync`, { method: "POST" });
      setError("");
      await (id === openIdRef.current ? refreshAll() : load());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const created = async (connection) => {
    setShowCreate(false);
    await load();
    if (connection && connection.id) openPanel(connection);
  };

  const deleted = async () => {
    closePanel();
    await load();
  };

  return (
    <div className="ds-page">
      <header className="ds-page-header cx-header">
        <div>
          <h2 className="ds-page-title">{t("adminConnectors.title")}</h2>
          <p className="ds-page-subtitle">{t("adminConnectors.subtitle")}</p>
        </div>
        {!createOpen && (
          <button type="button" className="ds-btn ds-btn--primary" onClick={() => setShowCreate(true)}>
            <Plus size={15} aria-hidden="true" />
            {t("adminConnectors.newTitle")}
          </button>
        )}
      </header>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}

      {createOpen && (
        <NewConnectionForm
          kinds={kinds}
          onCreated={created}
          onCancel={isEmpty ? undefined : () => setShowCreate(false)}
        />
      )}

      <div className={`cx-layout${openConnection ? " has-detail" : ""}`}>
        <section className="cx-list" aria-label={t("adminConnectors.title")}>
          {loading ? <div className="ds-empty">{t("adminConnectors.loading")}</div> : isEmpty ? (
            <div className="ds-empty cx-empty">
              <strong>{t("adminConnectors.empty")}</strong>
              <span>{t("adminConnectors.emptyHint")}</span>
            </div>
          ) : (
            <div className="ds-table-wrap">
              <table className="ds-table">
                <thead>
                  <tr>
                    <th>{t("adminConnectors.colName")}</th>
                    <th>{t("adminConnectors.colType")}</th>
                    <th className="cx-col-optional">{t("adminConnectors.colWorkspace")}</th>
                    <th>{t("adminConnectors.colStatus")}</th>
                    <th className="cx-col-optional">{t("adminConnectors.colLastActivity")}</th>
                    <th><span className="ds-visually-hidden">{t("adminConnectors.actionsTitle")}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {connectors.map((c) => (
                    <tr key={c.id} className={c.id === openId ? "ac-row-selected" : undefined}>
                      <td>
                        <div className="ac-cell-strong">{c.name}</div>
                        <div className="ds-meta ds-mono">{c.slug}</div>
                      </td>
                      <td>
                        <div>{kindLabel(c)}</div>
                        <div className="cx-caps">
                          {(c.capabilities || []).map((cap) => (
                            <span key={cap} className="ds-badge">{capabilityHint(cap, t)}</span>
                          ))}
                        </div>
                      </td>
                      <td className="cx-col-optional">{c.database}</td>
                      <td><ConnectionStatus connection={c} /></td>
                      <td className="cx-col-optional"><LastActivity connection={c} /></td>
                      <td>
                        <div className="cx-row-actions">
                          {hasSource(c) && (
                            <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                                    disabled={busyId === c.id || c.status === "running"}
                                    onClick={() => syncNow(c.id)}>
                              {t("adminConnectors.syncNow")}
                            </button>
                          )}
                          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                                  aria-pressed={c.id === openId}
                                  onClick={() => (c.id === openId ? closePanel() : openPanel(c))}>
                            {t("adminConnectors.manage")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {nextCursor && (
            <button type="button" className="ds-btn ds-btn--secondary ac-load-more" onClick={loadMore}>
              {t("adminConnectors.loadMore")}
            </button>
          )}
        </section>

        {openConnection && (
          <div className="cx-detail-col" data-testid="connection-detail">
            <ConnectionDetail
              key={openConnection.id}
              connection={openConnection}
              kind={openKind}
              roles={roles}
              onChanged={refreshAll}
              onClose={closePanel}
              onDeleted={deleted}
            />
          </div>
        )}
      </div>

      <RuntimeConnections />
    </div>
  );
}
